import { Icon, Panel, PanelGroup, Toggle } from "rsuite";
import PropTypes from "prop-types";
import React, { useState, useContext } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import Slider from "react-slick";
import SliderArrow from "../common/SliderArrows";
import {
  desktopSrvPanelHeight,
  srvImgStyles,
  srvPanelStyles,
  ServiceHeader,
  AccommodationPanel,
  ServicePanel,
} from "./helpers";
import { StyleTypeContext } from "../../umbilical_tools";
import { cStyler } from "../../helpers";
import tourIconMobileType2 from "../icons/tourIconMobileType2.svg";
import sanitizeHtml from "sanitize-html";
import { TranslationContext } from "../../umbilical_tools";
import { InlineIcon } from "@iconify/react";
import jssVariables, { buttonStylesType1 } from "../../jss_setup";

const trfPanelStyles = createUseStyles({
  panel: { ...srvPanelStyles },
  img: { ...srvImgStyles },
  [`@media ${jssVariables.media.smallscreen}`]: {
    panel: {
      height: desktopSrvPanelHeight,
    },
  },
});

const TransferPanel = (props) => {
  const { dayIdx, item, eventType } = props;
  const { type, brand } = useContext(StyleTypeContext);

  const classes = trfPanelStyles();
  return _.get(item, "transfers", [])
    .filter((trf) => {
      return trf.events[dayIdx] === eventType;
    })
    .map((trf, idx) => {
      const event = trf.events[dayIdx];
      return type !== "type2" ? (
        <Panel
          className={classes.panel}
          key={idx}
          shaded
          header={
            <ServiceHeader
              icon="bus"
              title={
                event === "inbound" ? "Airport to Hotel" : "Hotel to Airport"
              }
              event="Transfer"
            />
          }>
          <img
            className={classes.img}
            alt=""
            src={_.get(trf, `${event}.vehicle.images.0.url`, "")}
          />
        </Panel>
      ) : (
        <ServicePanel
          key={idx}
          icon="car"
          title={_.get(trf, "destData.name_en", "")}
          event={_.startCase(event)}
          time={
            event === "inbound"
              ? _.get(trf, "inbound.start", "")
              : _.get(trf, "outbound.start", "")
          }
          customStyles={cStyler({
            type,
            brand,
            objName: "servicePanel",
          })}
        />
      );
    });
};

const iconPseudo = {
  position: "absolute",
  content: '""',
  width: "30%",
  height: "4px",
  background: jssVariables.colors.text.dark,
  top: "50%",
  left: "50%",
};
const flightPanelStyles = createUseStyles({
  panel: {
    ...srvPanelStyles,
  },
  content: {
    display: "grid",
    gridTemplateRows: "1fr auto auto",
    justifyItems: "center",
  },
  iconContainer: {
    display: "grid",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyItems: "center",
    gridTemplateRows: "1fr auto",
    position: "relative",
    "&:before": {
      ...iconPseudo,
      transform: "translate(-125%, -50%)",
    },
    "&:after": {
      ...iconPseudo,
      transform: "translate(25%, -50%)",
    },
  },
  iconInnerContainer: {
    borderRadius: "50%",
    height: `calc(${jssVariables.normal_gap} * 3.5)`,
    width: `calc(${jssVariables.normal_gap} * 3.5)`,
    border: `2px solid ${jssVariables.colors.text.dark}`,
    padding: `calc(${jssVariables.normal_gap} * .55)`,
    fontSize: "small",
  },
  icon: {
    transform: "rotate(0.125turn)",
    fontSize: "x-large",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    panel: {
      height: desktopSrvPanelHeight,
    },
  },
});

const FlightPanel = (props) => {
  const { dayIdx, item, eventType } = props;
  const { type, brand } = useContext(StyleTypeContext);

  const classes = flightPanelStyles();

  return _.get(item, "flights", [])
    .filter((flight) => flight.events[dayIdx] === eventType)
    .map((flight, idx) => {
      const event = flight.events[dayIdx];

      return type !== "type2" ? (
        <Panel
          className={classes.panel}
          key={idx}
          header={
            <ServiceHeader
              icon="plane"
              title={flight.destData.name_en}
              event={_.startCase(event)}
            />
          }
          shaded>
          <div className={classes.content}>
            <div className={classes.iconContainer}>
              <div className={classes.iconInnerContainer}>
                <InlineIcon icon="fa:plane" className={classes.icon} />
              </div>{" "}
            </div>
            <img
              alt=""
              src={_.get(flight, "inbound_operating_airline.logo", "")}
            />
            <span>
              <strong>{flight.inbound_operating_airline.name}</strong>
            </span>
          </div>
        </Panel>
      ) : (
        <ServicePanel
          key={idx}
          icon="plane"
          title={flight.destData.name_en}
          event={_.startCase(event)}
          time={
            event === "arrival"
              ? _.last(flight.legs)?.[0]?.arrival ?? ""
              : flight.legs[0][0].arrival
          }
          customStyles={cStyler({
            type,
            brand,
            objName: "servicePanel",
          })}
        />
      );
    });
};

const addOnPanelStyles = createUseStyles({
  panel: (props) => ({
    ...srvPanelStyles,
    ..._.get(props, "customStyles.panel", {}),
  }),
  img: (props) => ({
    ...srvImgStyles,
    ..._.get(props, "customStyles.img", {}),
  }),
  detailsBtn: (props) => ({
    ...buttonStylesType1,
    zIndex: 1,
    height: "max-content",
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} * 1)`,
    alignItems: "center",
    padding: `calc(${jssVariables.normal_gap} * .7) calc(${jssVariables.normal_gap} * 1)`,
    justifySelf: "start",
    ..._.get(props, "customStyles.detailsBtn", {}),
  }),
  icon: (props) => ({
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    panel: (props) => ({
      height: desktopSrvPanelHeight,
      ..._.get(props, "customStyles.panel", {}),
    }),
  },
});

const AddonsPanel = (props) => {
  const { item, dayIdx, onViewDetails } = props;
  const { type, brand } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);

  const classes = addOnPanelStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "addOnPanel",
    }),
  });
  return _.get(item, "addons", []).map((addon, idx) => {
    var img = _.get(
      _.flattenDeep(
        _.get(addon, "items", [])
          .filter((item) =>
            item.data.custom_images.some((img) => img.photo_sm_url)
          )
          .map((item) => item.data.custom_images.map((img) => img.photo_sm_url))
      ),
      0,
      ""
    );

    if (!img) {
      img = _.get(
        _.flattenDeep(
          _.get(addon, "items", [])
            .filter((item) =>
              item.data.image_set.some((img) => img.photo_sm_url)
            )
            .map((item) => item.data.image_set.map((img) => img.photo_sm_url))
        ),
        0,
        ""
      );
    }

    return (
      type === "type2" && (
        <Panel
          shaded
          key={idx}
          header={
            <ServiceHeader
              title={addon.title}
              icon="map-marker"
              event=""
              customStyles={cStyler({
                type,
                brand,
                objName: "serviceHeader",
              })}
            />
          }
          className={classes.panel}>
          <img className={classes.img} alt="" src={img} />
          <button
            className={classes.detailsBtn}
            onClick={() => onViewDetails(dayIdx, addon.search_uid, "addons")}>
            {/* <InlineIcon icon="fa-solid:info-circle" /> */}
            <Icon icon="info" />
            {t("viewDetails")}
          </button>
        </Panel>
      )
    );
  });
};

const addOnPanelType2Styles = createUseStyles({
  addOnContainer: (props) => ({
    display: "grid",
    gridTemplateRows: "max-content max-content",
    background: jssVariables.colors.base_colorType2,
    color: "white",

    fontSize: "medium",
    fontWeight: "bold",
    ..._.get(props, "customStyles.addOnContainer", {}),
  }),
  addOn: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    justifyItems: "center",
    ..._.get(props, "customStyles.addOn", {}),
  }),
  title: (props) => ({
    fontSize: "large",
    fontWeight: "bold",

    ..._.get(props, "customStyles.title", {}),
  }),
  time: (props) => ({
    color: "#62BFC0",
    fontSize: "medium",

    ..._.get(props, "customStyles.time", {}),
  }),
  detailsBtnContainer: (props) => ({
    background: "#62bfc0",
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    height: "max-content",
    ..._.get(props, "customStyles.detailsBtnContainer", {}),
  }),
  detailsBtn: (props) => ({
    background: jssVariables.colors.base_colorType2,
    borderRadius: 0,

    fontSize: "large",
    textTransform: "uppercase",
    color: "white",
    padding: `calc(${jssVariables.normal_gap} * .8) calc(${jssVariables.normal_gap} * 5)`,
    ..._.get(props, "customStyles.detailsBtn", {}),
  }),
  icon: (props) => ({
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    addOnContainer: (props) => ({
      ..._.get(props, "customStyles.addOnContainer.smallscreen", {}),
    }),
  },
});

const AddonsPanelType2 = (props) => {
  const { item, dayIdx, onViewDetails } = props;
  const { type, brand } = useContext(StyleTypeContext);

  const classes = addOnPanelType2Styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "addOn",
    }),
  });
  return _.get(item, "addons", []).map((addon, idx) => {
    return (
      <div className={classes.addOnContainer} key={idx}>
        <div className={classes.addOn}>
          <span className={classes.data}>
            <span
              className={classes.title}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(_.get(addon, "title", "")),
              }}></span>
            {/* <span className={classes.time}>
              {DateTime.fromISO(
                _.get(addon, "items[0].arrival_time", "")
              ).toLocaleString(DateTime.TIME_24_SIMPLE)}
            </span> */}
          </span>

          <img className={classes.icon} alt="" src={tourIconMobileType2} />
        </div>
        <div className={classes.detailsBtnContainer}>
          <button
            className={classes.detailsBtn}
            onClick={() => onViewDetails(dayIdx, addon.search_uid, "addons")}>
            Details
          </button>
        </div>
      </div>
    );
  });
};

const ServicesList = (props) => {
  const { dayIdx, item, onViewDetails } = props;
  const { type, brand } = useContext(StyleTypeContext);

  return (
    <React.Fragment>
      <AccommodationPanel
        eventType="check_out"
        item={item}
        dayIdx={Number(dayIdx)}
        customStyles={cStyler({
          type,
          brand,
          objName: "servicePanel",
        })}
      />
      <TransferPanel eventType="outbound" item={item} dayIdx={dayIdx} />
      <FlightPanel eventType="departure" item={item} dayIdx={dayIdx} />
      <FlightPanel eventType="arrival" item={item} dayIdx={dayIdx} />
      <TransferPanel eventType="inbound" item={item} dayIdx={dayIdx} />
      <AccommodationPanel
        eventType="check_in"
        item={item}
        dayIdx={Number(dayIdx)}
        customStyles={cStyler({
          type,
          brand,
          objName: "servicePanel",
        })}
      />
      {type === "type2" && (
        <AddonsPanelType2
          dayIdx={dayIdx}
          item={item}
          onViewDetails={onViewDetails}
        />
      )}

      {type !== "type2" && (
        <AddonsPanel
          dayIdx={dayIdx}
          item={item}
          onViewDetails={onViewDetails}
        />
      )}
    </React.Fragment>
  );
};

const itinStyles = createUseStyles({
  services: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(6, 95%)",
    overflowX: "auto",
    overflowY: "hidden",
    padding: `calc(${jssVariables.normal_gap} / 2)`,
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.services", {}),
  }),
  serviceToggler: (props) => ({
    display: "grid",
    gridAutoFlow: "column",
    justifyContent: "end",
    gridAutoColumns: "max-content",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.serviceToggler", {}),
  }),
  mobileRender: (props) => ({
    ..._.get(props, "customStyles.mobileRender", {}),
  }),
  desktopRender: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktopRender", {}),
  }),
  description: (props) => ({
    display: "grid",
    gridTemplateRows: "auto auto",
    ..._.get(props, "customStyles.description", {}),
  }),
  descriptionTxt: (props) => ({
    display: "grid",
    maxHeight: props.expanded ? "100%" : "8rem",
    overflow: "scroll",
    transition: ".5s",
    ..._.get(props, "customStyles.descriptionTxt", {}),
  }),

  descTxtBtn: (props) => ({
    ..._.get(props, "customStyles.descTxtBtn", {}),
  }),
  slider: (props) => ({
    display: "grid",
    width: "100%",
    height: "30vh",
    ..._.get(props, "customStyles.slider", {}),
  }),
  img: (props) => ({
    objectFit: "cover",
    height: "30vh",
    width: "100%",
    borderRadius: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.img", {}),
  }),
  servicesContainer: (props) => ({
    display: "grid",
    ..._.get(props, "customStyles.servicesContainer", {}),
  }),

  servicesHeader: (props) => ({
    ..._.get(props, "customStyles.servicesHeader", {}),
  }),

  [`@media ${jssVariables.media.smallscreen}`]: {
    services: (props) => ({
      gridTemplateColumns: "repeat(6, 40%)",
      ..._.get(props, "customStyles.services.smallscreen", {}),
    }),
    serviceToggler: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.serviceToggler.smallscreen", {}),
    }),
    mobileRender: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.mobileRender.smallscreen", {}),
    }),
    desktopRender: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.desktopRender.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    services: (props) => ({
      gridTemplateColumns: "repeat(6, 30%)",
      ..._.get(props, "customStyles.services.bigscreen", {}),
    }),
  },
});
const ItineraryItemContent = (props) => {
  const [expanded, setExpanded] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const { dayIdx, item, onViewDetails } = props;
  const { type, brand } = useContext(StyleTypeContext);

  const classes = itinStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "itineraryItemContent",
    }),
    type,
    expanded,
  });

  const imgs = _.get(item, "images", []).map((img, idx) => img.photo_sm_url);

  const settings = {
    autoplay: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SliderArrow icon="fa:angle-right" />,
    prevArrow: <SliderArrow icon="fa:angle-left" />,
  };

  return (
    <React.Fragment>
      {type !== "type2" && (
        <div className={classes.serviceToggler}>
          <span>
            <strong>Services Details</strong>
          </span>
          <Toggle
            checked={showServices}
            onChange={() => setShowServices(!showServices)}
          />
        </div>
      )}

      <div className={classes.mobileRender}>
        {showServices ? (
          <div className={classes.services}>
            <ServicesList
              dayIdx={dayIdx}
              item={item}
              onViewDetails={onViewDetails}
            />
          </div>
        ) : (
          <div className={classes.description}>
            <div className={classes.descriptionTxt}>
              {_.get(item, "description.en", "")}
            </div>
            {type === "type2" && (
              <button
                onClick={() => setExpanded(!expanded)}
                className={classes.descTxtBtn}>
                <React.Fragment>{expanded ? "Less" : "More"}</React.Fragment>
              </button>
            )}
            {type === "type2" && (
              <React.Fragment>
                {imgs && (
                  <Slider className={classes.slider} {...settings}>
                    {imgs.map((img, idx) => (
                      <img className={classes.img} key={idx} alt="" src={img} />
                    ))}
                  </Slider>
                )}
                {(!!_.get(item, `flights[0].events[${dayIdx}]`, "") ||
                  (!!_.get(item, `transfers[0].events[${dayIdx}]`, "") &&
                    _.get(item, `transfers[0].events[${dayIdx}]`, "")) !==
                    "stay" ||
                  !!_.get(item, `accommodations[0].events[${dayIdx}]`, "")) && (
                  <div className={classes.servicesContainer}>
                    <div className={classes.servicesHeader}>Services</div>
                    <div className={classes.services}>
                      <ServicesList
                        dayIdx={dayIdx}
                        item={item}
                        onViewDetails={onViewDetails}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        )}
      </div>
      <div className={classes.desktopRender}>
        {type === "type2" ? (
          <React.Fragment>
            <div className={classes.descriptionTxt}>
              {_.get(item, "description.en")}
            </div>
            {_.get(item, "description.en") && (
              <button
                onClick={() => setExpanded(!expanded)}
                className={classes.descTxtBtn}>
                <React.Fragment>{expanded ? "Less" : "More"}</React.Fragment>
              </button>
            )}
            {imgs.length > 0 && (
              <Slider className={classes.slider} {...settings}>
                {imgs.map((img, idx) => (
                  <img className={classes.img} key={idx} alt="" src={img} />
                ))}
              </Slider>
            )}
            <div className={classes.servicesContainer}>
              <div className={classes.servicesHeader}>Services</div>
              <div className={classes.services}>
                <ServicesList
                  dayIdx={dayIdx}
                  item={item}
                  onViewDetails={onViewDetails}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <div className={classes.services}>
            <ServicesList
              dayIdx={dayIdx}
              item={item}
              onViewDetails={onViewDetails}
            />
          </div>
        )}
        {type !== "type2" && <div>{_.get(item, "description.en", "")}</div>}
      </div>
    </React.Fragment>
  );
};

ItineraryItemContent.propTypes = {
  dayIdx: PropTypes.string,
  item: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

const pkgItinStyles = createUseStyles({
  itin: (props) => ({
    "& .rs-panel-group": {
      border: `4px solid ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.itin", {}),
  }),
  header: (props) => ({
    margin: `0 5vw`,
    ..._.get(props, "customStyles.header", {}),
  }),
  panelgroup: (props) => ({
    margin: "5vw",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.panelgroup", {}),
  }),
  item: (props) => ({
    "& > .rs-panel-heading": {
      "&:before": {
        color: jssVariables.colors.text.dark,
      },
    },
    "& > .rs-panel-body": {
      display: "grid",
      gridGap: jssVariables.normal_gap,
    },
    ..._.get(props, "customStyles.item", {}),
  }),
  itemHeader: (props) => ({
    display: "inline-grid",
    gridTemplateColumns: props.type !== "type2" ? "1fr" : "auto 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    alignItems: "center",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.itemHeader", {}),
  }),
  imgHeader: (props) => ({
    width: "4rem",
    height: "4rem",
    objectFit: "cover",
    borderRadius: "50%",
    ..._.get(props, "customStyles.imgHeader", {}),
  }),
  itemSubHeader: (props) => ({
    "& small": {
      color: jssVariables.colors.text.dark,
    },
    ..._.get(props, "customStyles.itemSubHeader", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    header: (props) => ({
      margin: `${jssVariables.spacing.center_margin_large}`,
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    panelgroup: (props) => ({
      margin: jssVariables.spacing.center_padding_large,
      ..._.get(props, "customStyles.panelgroup.smallscreen", {}),
    }),
    imgHeader: (props) => ({
      ..._.get(props, "customStyles.imgHeader.smallscreen", {}),
    }),
  },
});

const PackageItinerary = (props) => {
  const { dayByDayData, onViewDetails, details } = props;
  const { t } = useContext(TranslationContext);

  const { type, brand } = useContext(StyleTypeContext);

  if (!dayByDayData) {
    return null;
  }

  var duration = 0;
  if (type === "type2") {
    if (
      details.approximate_duration &&
      Object.values(details.approximate_duration).reduce(
        (a, b) => Number.parseInt(b, 10) + a,
        0
      ) > 0
    ) {
      const hours = Number.parseInt(details.approximate_duration.hours);
      const minutes = Number.parseInt(details.approximate_duration.minutes);

      duration = Math.floor(60 * (hours * 60 + minutes));
    } else {
      duration = Math.floor(
        _.get(details, "items", []).reduce(
          (a, b) =>
            a +
            _.get(b, "visit_duration", 0) * 60 +
            _.get(b, "departure_route.duration", 0),
          0
        )
      );
    }
  }

  function getImageIndex(images, dayIdx) {
    return images.length % dayIdx;
  }

  const classes = pkgItinStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "packageItineraryStyles",
    }),
    type,
  });

  return (
    <div className={classes.itin}>
      <h4 className={classes.header}>{t("itinerary")}</h4>
      {type === "type2" && (
        <span className={classes.subHeader}>
          {duration ? `Total Duration: ~${duration}` : ""}
        </span>
      )}

      <PanelGroup
        className={classes.panelgroup}
        accordion
        defaultActiveKey={1}
        bordered>
        {Object.entries(dayByDayData).map((entry, idx) => {
          const dayIdx = parseInt(entry[0], 10);
          const dayData = entry[1];
          return (
            <Panel
              key={idx}
              className={classes.item}
              header={
                <div className={classes.itemHeader}>
                  {type !== "type1" && (
                    <img
                      className={classes.imgHeader}
                      src={_.get(
                        dayData,
                        `destinations[0].images[${getImageIndex(
                          _.get(dayData, "destinations[0].images", []),
                          dayIdx
                        )}].thumbnail_url`,
                        ""
                      )}
                      alt=""
                    />
                  )}

                  <div className={classes.txtHeader}>
                    <h5>Day {dayIdx} </h5>
                    <h6 className={classes.itemSubHeader}>
                      <small>
                        {dayData["destinations"]
                          .map(
                            (dest) =>
                              `${_.startCase(dest.events[dayIdx])}: ${
                                dest.name_en
                              }`
                          )
                          .join(", ")}
                      </small>
                    </h6>
                  </div>
                </div>
              }
              eventKey={idx + 1}>
              <ItineraryItemContent
                dayIdx={dayIdx.toString()}
                item={dayData}
                onViewDetails={onViewDetails}
              />
            </Panel>
          );
        })}
      </PanelGroup>
    </div>
  );
};

PackageItinerary.defaultProps = {
  details: {},
};

PackageItinerary.propTypes = {
  details: PropTypes.object.isRequired,
  dayByDayData: PropTypes.object,
  onViewDetails: PropTypes.func.isRequired,
};

export default PackageItinerary;
